// =============================
// src/pages/materials/out/Out_new.jsx
// =============================
import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import { v4 as uuid } from "uuid";

import FormContentManager from "../../../components/common/form/FormContentManager";
import TableCell from "../../../components/common/ui/TableCell";
import { getStockSummary } from "../../../api/material/extras";

/* ======================================================
   🔹 재고 선택 모달
====================================================== */
function StockSelectModal({ projectId, onSelect, onClose }) {
  const [query, setQuery] = useState("");
  const [list, setList] = useState([]);

  useEffect(() => {
    async function load() {
      const res = await getStockSummary({
        project: projectId,
        q: query,
        page_size: 9999,
      });
      setList(res.items || res.results || []);
    }
    load();
  }, [query, projectId]);

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
      <div className="bg-white p-4 rounded shadow-xl w-[700px] max-h-[80vh] overflow-auto">
        <div className="flex justify-between mb-3">
          <input
            className="border px-2 py-1 w-64"
            placeholder="코드 / 품명 검색"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <button className="btn-basic-sm" onClick={onClose}>
            닫기
          </button>
        </div>

        <table className="w-full text-sm border table-fixed">
          <thead className="bg-gray-100">
            <tr>
              <th className="border px-2 py-1">코드</th>
              <th className="border px-2 py-1">품명</th>
              <th className="border px-2 py-1 w-[70px]">단위</th>
              <th className="border px-2 py-1 text-right w-[100px]">재고</th>
              <th className="border px-2 py-1 w-[60px]"></th>
            </tr>
          </thead>
          <tbody>
            {list.map((m) => (
              <tr key={m.item_id} className="border-t hover:bg-gray-50">
                <td className="border px-2 py-1">{m.item_code}</td>
                <td className="border px-2 py-1">{m.item_name}</td>
                <td className="border px-2 py-1 text-center">{m.unit}</td>
                <td className="border px-2 py-1 text-right">
                  {Number(m.qty_on_hand).toLocaleString()}
                </td>
                <td className="border px-2 py-1 text-center">
                  <button className="btn-basic-sm" onClick={() => onSelect(m)}>
                    선택
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ======================================================
   🔹 Out Form
====================================================== */
function OutForm({ value, onChange, projectId }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [targetRow, setTargetRow] = useState(null);

  const header = value.header;

  const makeRow = () => ({
    _id: uuid(),
    item_id: null,
    code: "",
    name: "",
    unit: "",
    qty: "",
    stock_qty: 0,
    remark: "",
  });

  // 🔥 최소 8행 보장
  useEffect(() => {
    if (value.items.length >= 8) return;

    onChange((prev) => {
      const padded = [...prev.items];
      while (padded.length < 8) padded.push(makeRow());
      return { ...prev, items: padded };
    });
  }, [value.items, onChange]);

  const setHeader = (k, v) =>
    onChange({ ...value, header: { ...value.header, [k]: v } });

  const updateRow = (id, key, val) =>
    onChange({
      ...value,
      items: value.items.map((r) => (r._id === id ? { ...r, [key]: val } : r)),
    });

  const removeRow = (id) =>
    onChange((prev) => {
      const filtered = prev.items.filter((r) => r._id !== id);
      while (filtered.length < 8) filtered.push(makeRow());
      return { ...prev, items: filtered };
    });

  const applyStock = (it) => {
    onChange((prev) => ({
      ...prev,
      items: prev.items.map((r) =>
        r._id === targetRow
          ? {
              ...r,
              item_id: it.item_id,
              code: it.item_code,
              name: it.item_name,
              unit: it.unit,
              stock_qty: Number(it.qty_on_hand),
            }
          : r
      ),
    }));
    setModalOpen(false);
  };

  return (
    <div>
      {/* HEADER */}
      <div className="overflow-hidden mb-5">
        <table className="w-full text-sm">
          <tbody>
            {[
              ["제목", "title"],
              ["출고일", "issue_date", "date"],
              ["사용처 / 현장", "to_site"],
              ["창고", "warehouse"],
              ["출고 사유", "reason"],
            ].map(([label, key, type]) => (
              <tr key={key}>
                <TableCell
                  label={label}
                  editing
                  colSpan={3}
                  input={
                    <input
                      type={type || "text"}
                      className="w-full border px-2 py-1"
                      value={header[key]}
                      onChange={(e) => setHeader(key, e.target.value)}
                    />
                  }
                />
              </tr>
            ))}

            <tr>
              <TableCell
                label="비고"
                editing
                colSpan={3}
                input={
                  <textarea
                    className="w-full min-h-[80px] border px-2 py-1"
                    value={header.note}
                    onChange={(e) => setHeader("note", e.target.value)}
                  />
                }
              />
            </tr>
          </tbody>
        </table>
      </div>

      {/* ITEMS */}
      <div>
        <div className="flex justify-between mb-2">
          <span className="text-body-bold">출고 품목</span>
          <button
            className="btn-basic-sm"
            onClick={() =>
              onChange((prev) => ({
                ...prev,
                items: [...prev.items, makeRow()],
              }))
            }
          >
            + 행 추가
          </button>
        </div>

        <table className="w-full text-sm border table-fixed">
          <colgroup>
            <col /> {/* 코드 */}
            <col /> {/* 품명 */}
            <col style={{ width: "70px" }} /> {/* 단위 */}
            <col style={{ width: "90px" }} /> {/* 수량 */}
            <col /> {/* 비고 */}
            <col style={{ width: "70px" }} /> {/* 선택 */}
            <col style={{ width: "70px" }} /> {/* 삭제 */}
          </colgroup>

          <thead className="bg-gray-100 border-b">
            <tr>
              <th className="border px-2 py-1">코드</th>
              <th className="border px-2 py-1">품명</th>
              <th className="border px-2 py-1 text-center">단위</th>
              <th className="border px-2 py-1 text-right">수량</th>
              <th className="border px-2 py-1">비고</th>
              <th className="border px-2 py-1 text-center">선택</th>
              <th className="border px-2 py-1 text-center">삭제</th>
            </tr>
          </thead>

          <tbody>
            {value.items.map((r) => (
              <tr key={r._id} className="border-t">
                <td className="border px-2 py-1">{r.code}</td>
                <td className="border px-2 py-1">{r.name}</td>
                <td className="border px-2 py-1 text-center">{r.unit}</td>

                <td className="border px-2 py-1 text-right">
                  <input
                    type="number"
                    className="w-full border px-1 text-right"
                    value={r.qty}
                    onChange={(e) => {
                      const v = Number(e.target.value || 0);
                      if (v > r.stock_qty) {
                        alert(
                          `출고 수량은 재고(${r.stock_qty})를 초과할 수 없습니다.`
                        );
                        return;
                      }
                      updateRow(r._id, "qty", v);
                    }}
                  />
                </td>

                <td className="border px-2 py-1">
                  <input
                    className="w-full border px-1"
                    value={r.remark}
                    onChange={(e) => updateRow(r._id, "remark", e.target.value)}
                  />
                </td>

                {/* 선택 */}
                <td className="border px-2 py-1 text-center">
                  <button
                    className="btn-basic-sm"
                    onClick={() => {
                      setTargetRow(r._id);
                      setModalOpen(true);
                    }}
                  >
                    선택
                  </button>
                </td>

                {/* 삭제 */}
                <td className="border px-2 py-1 text-center">
                  <button
                    className="doc-delete"
                    onClick={() => removeRow(r._id)}
                  >
                    삭제
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modalOpen && (
        <StockSelectModal
          projectId={projectId}
          onSelect={applyStock}
          onClose={() => setModalOpen(false)}
        />
      )}
    </div>
  );
}

/* ======================================================
   🔹 Out_new
====================================================== */
export default function Out_new() {
  const { id: projectId, dns } = useParams();

  const [form, setForm] = useState({
    header: {
      title: "출고서(공식)",
      issue_date: new Date().toISOString().slice(0, 10),
      to_site: "",
      warehouse: "",
      reason: "",
      note: "",
    },
    items: [],
  });

  const summaryConfig = useMemo(
    () => ({
      title: "출고서 요약",
      sections: [
        { label: "제목", value: form.header.title },
        { label: "출고일", value: form.header.issue_date },
        { label: "사용처", value: form.header.to_site },
        { label: "창고", value: form.header.warehouse },
        { label: "사유", value: form.header.reason },
      ],
      list: form.items.map((i) => ({
        label: `${i.name} (${i.code})`,
        value: `${i.qty}`,
      })),
    }),
    [form]
  );

  const getBody = useCallback(() => {
    return {
      values: {
        project: projectId,
        module: "material",
        doc_type: "GI",
        dns,
        title: form.header.title,
        header: form.header,
        lines: form.items.map(({ _id, ...r }) => r),
        summary: summaryConfig,
      },
    };
  }, [form, projectId, dns, summaryConfig]);

  return (
    <FormContentManager
      params={{ module: "material", doc_type: "GI", project: projectId, dns }}
      getBody={getBody}
      summaryConfig={summaryConfig}
      docName="출고서"
      redirectTo={`/project/${projectId}/material/materialboard`}
    >
      <OutForm value={form} onChange={setForm} projectId={projectId} />
    </FormContentManager>
  );
}
